/**
 * Pure helpers for the Scorecard tab: which days to compare, and how a metric
 * and its change read. The server counts calendar days in the timezone we
 * send, so days travel as `YYYY-MM-DD`, never as instants.
 */

export type ScorecardUnit = 'RATE' | 'COUNT' | 'AVERAGE';

export interface DayWindow {
  from: string;
  to: string;
}

export type ComparePreset = '7' | '28' | '90' | 'custom';

export const COMPARE_PRESETS: { key: ComparePreset; label: string }[] = [
  { key: '7', label: 'Last 7 days' },
  { key: '28', label: 'Last 28 days' },
  { key: '90', label: 'Last 90 days' },
  { key: 'custom', label: 'Pick dates' },
];

/** A local Date as the calendar day the browser shows for it. */
export function toDay(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function addDays(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/**
 * The last `days` complete days, ending yesterday because today is still in
 * progress, against the same number of days just before them.
 */
export function presetWindows(days: number, today: Date = new Date()): { current: DayWindow; previous: DayWindow } {
  const to = addDays(toDay(today), -1);
  const from = addDays(to, -(days - 1));
  return {
    current: { from, to },
    previous: { from: addDays(from, -days), to: addDays(from, -1) },
  };
}

function parseDay(day: string): Date {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, date));
}

/** "Oct 1 – Oct 27", with the year only where the two ends differ or it is not this year. */
export function formatDays(window: DayWindow, now: Date = new Date()): string {
  const from = parseDay(window.from);
  const to = parseDay(window.to);
  const thisYear = now.getFullYear();
  const showYear = from.getUTCFullYear() !== to.getUTCFullYear() || to.getUTCFullYear() !== thisYear;
  const format = (date: Date, year: boolean) =>
    date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(year ? { year: 'numeric' } : {}), timeZone: 'UTC' });
  if (window.from === window.to) return format(from, showYear);
  return `${format(from, showYear && from.getUTCFullYear() !== to.getUTCFullYear())} – ${format(to, showYear)}`;
}

/** 0.1342 → "13.4%". Rates always carry one decimal so two of them compare at a glance. */
export function formatRate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '—';
  return `${(rate * 100).toFixed(1)}%`;
}

export function formatMetric(unit: ScorecardUnit, value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  if (unit === 'RATE') return formatRate(value);
  if (unit === 'AVERAGE') return value.toLocaleString('en-US', { maximumFractionDigits: 1 });
  return Math.round(value).toLocaleString('en-US');
}

export type Tone = 'good' | 'bad' | 'flat' | 'none';

export interface Change {
  /** "+1.2 pts", "−37 (−12%)", "No change", or "" when there is nothing to compare. */
  label: string;
  direction: 'up' | 'down' | 'flat' | 'none';
  tone: Tone;
}

/**
 * The change from `previous` to `current`. A rate moves in percentage points,
 * because "+10%" of 13% reads as 23% to most people; a count or average moves
 * by its own amount, with the relative change after it.
 */
export function describeChange(
  unit: ScorecardUnit,
  current: number | null | undefined,
  previous: number | null | undefined,
  higherIsBetter = true,
): Change {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return { label: '', direction: 'none', tone: 'none' };
  }
  const delta = current - previous;
  const flat = unit === 'RATE' ? Math.abs(delta) < 0.0005 : Math.abs(delta) < (unit === 'AVERAGE' ? 0.05 : 0.5);
  if (flat) return { label: 'No change', direction: 'flat', tone: 'flat' };

  const sign = delta > 0 ? '+' : '−';
  let label: string;
  if (unit === 'RATE') {
    label = `${sign}${Math.abs(delta * 100).toFixed(1)} pts`;
  } else {
    const amount = unit === 'AVERAGE' ? Math.abs(delta).toFixed(1) : Math.round(Math.abs(delta)).toLocaleString('en-US');
    const relative = previous === 0 ? '' : ` (${sign}${Math.round(Math.abs(delta / previous) * 100)}%)`;
    label = `${sign}${amount}${relative}`;
  }
  const up = delta > 0;
  return { label, direction: up ? 'up' : 'down', tone: up === higherIsBetter ? 'good' : 'bad' };
}

/** Below this many members a rate swings on one or two people, and the tab says so. */
export const SMALL_GROUP = 30;

/** "of 305 members" for a rate, "" otherwise. */
export function basisOf(unit: ScorecardUnit, numerator: number | null, denominator: number | null): string {
  if (unit !== 'RATE' || denominator === null) return '';
  if (denominator === 0) return 'Nobody to count yet';
  return `${Math.round(numerator ?? 0).toLocaleString('en-US')} of ${Math.round(denominator).toLocaleString('en-US')}`;
}

/** 2.03 → "2.0×". */
export function formatLift(lift: number | null | undefined): string {
  if (lift === null || lift === undefined) return '—';
  return `${lift.toFixed(1)}×`;
}

/** The Scorecard's sections, in order, with the note under each; shared by the tab and its PDF. */
export const SCORECARD_SECTIONS: { key: string; title: string; note: string }[] = [
  {
    key: 'RETENTION',
    title: 'Do new members come back?',
    note: 'Counted by the day members registered. A rate waits until everyone in it has had the time it measures, so the slower ones look further back; the dates under a number say whose it is.',
  },
  {
    key: 'ACTIVATION',
    title: 'Do new members do what makes them stay?',
    note: 'What members did in their first week or two. These are the early signs that a member will still be here months later: see What makes members stay, below.',
  },
  {
    key: 'ACTIVITY',
    title: 'How many members use the app',
    note: 'Active means a member opened or used the app that day.',
  },
  { key: 'GROWTH', title: 'New members', note: '' },
  { key: 'ACTIONS', title: 'What members do', note: '' },
];

/** "2026-07-01" → "Q3 2026". */
export function quarterLabel(cohort: string): string {
  const [year, month] = cohort.split('-').map(Number);
  return `Q${Math.floor((month - 1) / 3) + 1} ${year}`;
}
